/* eslint-disable import/no-unassigned-import */
/* eslint-disable @typescript-eslint/no-restricted-imports */
/* eslint-disable @typescript-eslint/no-implicit-any-catch */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { Calendar } from '@livecontrol/core-utils';
import _ from 'lodash';
import type { DateTime, Interval } from 'luxon';
import moment from 'moment';
import { validateEventDates } from './dates-validator';
import { validateRepeatingEvent } from './repeat-validator';
import { Schedule } from './rschedule';
import { EventValidator } from './types';
import 'moment-timezone';

interface Props {
  newEventInfo: EventValidator.EventInfo;
  isAdmin?: boolean;
  timezone?: string;
  isProd: boolean;
  simulatedLiveAssetIsUpload: boolean;
}

export const RECURRENCE_MAP: Record<Calendar.Unit, 'DAILY' | 'MONTHLY' | 'WEEKLY'> = {
  [Calendar.Unit.DAYS]: 'DAILY',
  [Calendar.Unit.WEEKS]: 'WEEKLY',
  [Calendar.Unit.MONTHS]: 'MONTHLY'
};

interface RecurrenceRules {
  frequency?: 'DAILY' | 'MONTHLY' | 'WEEKLY';
  start?: moment.Moment;
  end?: moment.Moment;
  count?: number;
  byDayOfWeek?: [string, number][] | string[];
}


const explodeDates = (rules: any, timezone: string): string[] => {
  try {
      const schedule = new Schedule({
          timezone,
          rrules: [
              rules
          ]
      });

      const scheduleDates = schedule
          .occurrences()
          .toArray()
          .map(({ date }) => (<moment.Moment & Record<string, unknown>>date).tz('utc').toISOString());

      return scheduleDates;
  }  catch (error: any) {

      console.log('Error in explodeDates:', error);

      return [];
  }
};





const validateRecurringDates = (startTime: DateTime, endTime: DateTime, timezone: string, settings?: EventValidator.EventInfo.RepeatSettings ): EventValidator.Errors => {
  // let location_obj = this.locationRepository.findOneOrFail({ where:{id:payload.location}});

  if(!settings || !settings.repeat || !settings.repeatFrequency?.unit){
    return {};
  }

  const errors: EventValidator.Errors = {};

  const formatDaysOfWeek = (
    daysOfWeek: Set<Calendar.Weekday> | undefined
  ): string[] | undefined => {
    if (daysOfWeek) {
      return [...daysOfWeek].map((day) => day.toUpperCase().slice(0, 2));
    }

    return undefined;
  };

  const frequency = RECURRENCE_MAP[settings.repeatFrequency.unit];

  const recurrenceMode = (frequency === 'MONTHLY' && settings.repeatFrequency.monthMode) ?
    settings.repeatFrequency.monthMode :
    undefined;

  const recurrenceEndDate = (settings.repeatEndCondition === EventValidator.EventInfo.RepeatEndCondition.ON_DATE && settings.repeatUntil?.stopRepeatingOn) ?
    settings.repeatUntil.stopRepeatingOn.toJSDate() :
    undefined;

  const recurrenceEndOn = (settings.repeatEndCondition === EventValidator.EventInfo.RepeatEndCondition.AFTER_OCCURRENCES && settings.repeatUntil?.numRecurrences) ?
    Number(settings.repeatUntil.numRecurrences) :
    undefined;

  const repeatCondition = settings.repeatEndCondition;

  const recurrenceDaysOfWeek = settings.repeatFrequency.daysOfWeek ?
    formatDaysOfWeek(settings.repeatFrequency.daysOfWeek) :
    undefined;

  const startDateRules: RecurrenceRules = {
    frequency,
    start: moment.tz(startTime, timezone)
  };

  const endDateRules: RecurrenceRules = {
    frequency,
    start: moment.tz(endTime, timezone)
  };

  if (recurrenceEndDate) {
      startDateRules.end = moment.tz(recurrenceEndDate, timezone);
      endDateRules.end = moment.tz(recurrenceEndDate, timezone);
  }

  if (recurrenceEndOn) {
      startDateRules.count = recurrenceEndOn;
      endDateRules.count = recurrenceEndOn;
  }

  if (recurrenceDaysOfWeek && recurrenceDaysOfWeek.length > 0 && frequency === 'WEEKLY') {
      startDateRules.byDayOfWeek = recurrenceDaysOfWeek;
      endDateRules.byDayOfWeek = recurrenceDaysOfWeek;
  } else if (recurrenceMode && recurrenceMode === 'WEEKDAY') {

    const startDate = moment.tz(startTime, timezone);

    const weekdayIndex = startDate.weekday(); // 0–6 (Sun–Sat)
    const weekdays = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

    const firstOfMonth = startDate.clone().startOf('month');
    const dayOfMonth = startDate.date();

    const offset = (weekdayIndex - firstOfMonth.weekday() + 7) % 7;

    let position = Math.floor((dayOfMonth - 1 - offset) / 7) + 1;

    // We check if by adding 7 days we change months if not this is the last month occurrence
    const nextSameWeekday = startDate.clone().add(7, 'days');

    if(nextSameWeekday.month() !== startDate.month()){
        // last occurrence
        position = -1;
    }

    startDateRules.byDayOfWeek = [[weekdays[weekdayIndex], position]];
    endDateRules.byDayOfWeek = [[weekdays[weekdayIndex], position]];
  }


  const start_dates_to_schedule = explodeDates(startDateRules, timezone);

  const end_dates_to_schedule = explodeDates(endDateRules, timezone);

  if(start_dates_to_schedule.length === 0 || end_dates_to_schedule.length === 0){
    _.merge(errors, {
      repeatSettings: { repeatUntil: { numRecurrences:  EventValidator.ErrorMessages.NUM_RECURRENCES_INVALID} }
    });
  }

  // Check if any date ends on next year 
  const nextYear = moment.tz().add(1, 'year');

  const anyStartDateNextYear = start_dates_to_schedule.some(dateStr => {
    const date = moment.tz(dateStr, timezone);

    // If the difference is more than 365 days
    if(date.diff(nextYear, 'days') >= 0){
      return true;
    }


    return date >= (nextYear);
  });

  if(anyStartDateNextYear){
    switch(settings.repeatFrequency.unit){
      case Calendar.Unit.DAYS:
        _.merge(errors, {
          repeatSettings: { repeatUntil: { 
            numRecurrences: repeatCondition === EventValidator.EventInfo.RepeatEndCondition.ON_DATE ? undefined : EventValidator.ErrorMessages.DAYS_EXCEEDED,
            stopRepeatingOn: repeatCondition === EventValidator.EventInfo.RepeatEndCondition.ON_DATE ? EventValidator.ErrorMessages.DAYS_EXCEEDED : undefined
          } }
        });

        break;
      case Calendar.Unit.WEEKS:
        _.merge(errors, {
          repeatSettings: { repeatUntil: { 
            numRecurrences:  repeatCondition === EventValidator.EventInfo.RepeatEndCondition.ON_DATE ? undefined : EventValidator.ErrorMessages.WEEKS_EXCEEDED,
            stopRepeatingOn: repeatCondition === EventValidator.EventInfo.RepeatEndCondition.ON_DATE ? EventValidator.ErrorMessages.WEEKS_EXCEEDED : undefined
          } }
        });

        break;
      case Calendar.Unit.MONTHS:
        _.merge(errors, {
          repeatSettings: { repeatUntil: { 
            numRecurrences:  repeatCondition === EventValidator.EventInfo.RepeatEndCondition.ON_DATE ? undefined : EventValidator.ErrorMessages.MONTHS_EXCEEDED,
            stopRepeatingOn: repeatCondition === EventValidator.EventInfo.RepeatEndCondition.ON_DATE ? EventValidator.ErrorMessages.MONTHS_EXCEEDED : undefined
          } }
        });

        break;
    }
  } else if(start_dates_to_schedule.length >= 101 || end_dates_to_schedule.length >= 101){

    _.merge(errors, {
      repeatSettings: { repeatUntil: {
        numRecurrences:  repeatCondition === EventValidator.EventInfo.RepeatEndCondition.ON_DATE ? undefined : EventValidator.ErrorMessages.TOO_MANY_RECURRENCES,
        stopRepeatingOn: repeatCondition === EventValidator.EventInfo.RepeatEndCondition.ON_DATE ? EventValidator.ErrorMessages.TOO_MANY_RECURRENCES : undefined
      } }
    });
  }

  return errors;

};

export const getAllRecurrences = ({
  newEventInfo,
  simulatedLiveAssetIsUpload,
  isAdmin,
  isProd,
  timezone = 'UTC'
}: Props): { recurrences?: Interval[]; errors?: EventValidator.Errors } => {
  const errors = {
    ...validateEventDates({ newEventInfo, simulatedLiveAssetIsUpload, isAdmin, isProd }),
    ...validateRepeatingEvent(newEventInfo),
    ...validateRecurringDates(newEventInfo.start!, newEventInfo.end!, timezone,  newEventInfo.repeatSettings  )
  };

  if (_.isEmpty(errors)) {
    const { repeatSettings } = newEventInfo;
    const recurrences: Interval[] = [];

    if (repeatSettings?.repeat) {
      return { recurrences };
    }
  }

  return { errors };
};
